import type { MediaPolicy, MediaPressureLevel, MediaScreenTier } from "./types";

const tierRank: Record<MediaScreenTier, number> = { low: 0, medium: 1, high: 2 };
const levelCaps: Record<MediaPressureLevel, { tier: MediaScreenTier; previews: number }> = {
  normal: { tier: "high", previews: 15 },
  guard: { tier: "medium", previews: 8 },
  pressure: { tier: "low", previews: 4 },
  emergency: { tier: "low", previews: 2 },
};

function pressureLevel(value: unknown): MediaPressureLevel | null {
  return value === "normal" || value === "guard" || value === "pressure" || value === "emergency" ? value : null;
}

function screenTier(value: unknown): MediaScreenTier | null {
  return value === "low" || value === "medium" || value === "high" ? value : null;
}

function stricterTier(left: MediaScreenTier, right: MediaScreenTier): MediaScreenTier {
  return tierRank[left] <= tierRank[right] ? left : right;
}

export function conservativeMediaPolicy(nowMs = Date.now()): MediaPolicy {
  const now = new Date(nowMs).toISOString();
  return {
    version: 1,
    level: "emergency",
    nodeId: "unavailable",
    txMbps: 0,
    sampledAt: now,
    fresh: false,
    validUntil: now,
    fallbackLevel: "emergency",
    maxScreenTier: "low",
    maxCameraPreviews: 2,
  };
}

export function parseMediaPolicy(value: unknown, nowMs = Date.now()): MediaPolicy {
  if (!value || typeof value !== "object") return conservativeMediaPolicy(nowMs);
  const input = value as Record<string, unknown>;
  const level = pressureLevel(input.level);
  const fallbackLevel = pressureLevel(input.fallbackLevel);
  const declaredTier = screenTier(input.maxScreenTier);
  const validUntilMs = typeof input.validUntil === "string" ? Date.parse(input.validUntil) : Number.NaN;
  const sampledAtMs = typeof input.sampledAt === "string" ? Date.parse(input.sampledAt) : Number.NaN;
  if (input.version !== 1 || !level || !fallbackLevel || !declaredTier || input.fresh !== true || !Number.isFinite(validUntilMs) || validUntilMs <= nowMs || !Number.isFinite(sampledAtMs)) {
    return conservativeMediaPolicy(nowMs);
  }
  const declaredPreviews = typeof input.maxCameraPreviews === "number" && Number.isInteger(input.maxCameraPreviews)
    ? input.maxCameraPreviews
    : 0;
  if (declaredPreviews < 1) return conservativeMediaPolicy(nowMs);
  const cap = levelCaps[level];
  return {
    version: 1,
    level,
    nodeId: typeof input.nodeId === "string" ? input.nodeId : "unknown",
    txMbps: typeof input.txMbps === "number" && Number.isFinite(input.txMbps) && input.txMbps >= 0 ? input.txMbps : 0,
    sampledAt: new Date(sampledAtMs).toISOString(),
    fresh: true,
    validUntil: new Date(validUntilMs).toISOString(),
    fallbackLevel,
    maxScreenTier: stricterTier(declaredTier, cap.tier),
    maxCameraPreviews: Math.min(declaredPreviews, cap.previews, 15),
  };
}
